import { rgba } from 'polished';
import styled, { css } from 'styled-components';
import { applyStyleModifiers, type ModifiersConfig, type ModifierKeys } from 'styled-components-modifiers';

// Import styles
import { flex, customMinResponsive, size } from 'components/styles';

// Import types
import { Modifiers } from 'types/views/styles';

const MODIFIERS: Modifiers = {
	text: ({ theme }) => css`
		&:hover:not(:disabled) {
			color: ${theme.configurator.color.primary};
		}
		&:disabled {
			color: ${rgba(theme.configurator.color.primary, 0.6)};
		}
	`,
	contained: ({ theme }) => css`
		background: linear-gradient(0deg, ${theme.navyBlue} 0%, ${theme.blueAzureDark} 53.8%);
		color: ${theme.configurator.color.primary};

		&:hover:not(:disabled) {
			box-shadow: 0 0 6px ${theme.configurator.bg.contrast};
		}

		&:active {
			box-shadow: 0 0 2px ${theme.configurator.bg.dark};
		}
	`,
	outlined: ({ theme }) => css`
		border: 1px solid ${theme.configurator.color.highlight};
		background: ${rgba(theme.configurator.bg.dark, 0.05)};
	`,
	back: ({ theme }) => css`
		width: auto;
		color: ${rgba(theme.configurator.color.primary, 0.6)};
		padding: 2rem 1.4rem;
	`,
	no_transform: () => css`
		text-transform: none;
		letter-spacing: 0;
	`,
	info_bar: ({ theme }) => css`
		padding: 1rem 1.5rem;
		color: ${theme.palette.primary.main};
		letter-spacing: 0;
		text-align: center;
		font-size: 1.1rem;
		font-weight: ${theme.font.medium};
		background: transparent;

		&:hover {
			color: ${theme.configurator.color.highlight};
		}

		${customMinResponsive(
			size.semi,
			css`
				font-size: 1.4rem;
			`
		)}
	`,
	cancel: () => css`
		border: 1px solid rgba(224, 224, 224, 0.3);
		background: radial-gradient(
				157.86% 58.75% at 30.18% 5.11%,
				rgba(255, 255, 255, 0.2) 0%,
				rgba(155, 155, 155, 0.2) 36.68%,
				rgba(113, 112, 112, 0.2) 64.6%,
				rgba(78, 78, 78, 0.2) 100%
			),
			rgba(0, 0, 0, 0.3);
	`,
	primary_color: ({ theme }) => css`
		color: ${theme.configurator.color.primary};
	`,
	user_profile: ({ theme }) => css`
		width: 10.4rem;
		height: 3rem;
		font-size: 1.1rem !important;

		&:hover:not(:disabled) {
			filter: brightness(105%);
			transition: all 0s ease-in-out;
			box-shadow: 0px 0px 0.25rem ${theme.configurator.color.highlight};
		}
	`,
	small: () => css`
		${customMinResponsive(
			size.semi,
			css`
				font-size: 1.4rem;
			`
		)};
	`
};

export const StyledButton = styled.button<{ modifiers?: ModifierKeys }>`
	${flex()}
	border-radius: 0.4rem;
	padding: 1.05em 0;
	white-space: nowrap;
	text-transform: uppercase;
	font-weight: ${({ theme }) => theme.font.semiBold};
	font-size: 1.3rem;
	line-height: normal;
	letter-spacing: 1px;
	transition: all 300ms ease-in-out;
	width: 100%;
	border: none;
	background: transparent;
	color: ${({ theme }) => theme.configurator.color.highlight};
	cursor: pointer;

	&:disabled {
		opacity: 0.7;
		cursor: not-allowed;
	}

	${customMinResponsive(
		size.semi,
		css`
			font-size: 1.5rem;
		`
	)}

	${applyStyleModifiers(MODIFIERS as ModifiersConfig)};
`;
